import type React from 'react';
import { useEffect, useState } from 'react';

import { useDebounce } from 'ahooks';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import Button from '../../components/Button';

import Input, { type InputProps } from '../Input/Input';

interface InputSearchProps extends Omit<InputProps, 'onChange'> {
  initialValue?: string;
  debounceInMillis?: number;
  clearSearch?: boolean;
  onChange: (value: string) => void;
}

const InputSearch = ({ initialValue = '', debounceInMillis = 300, clearSearch, onChange, ...props }: InputSearchProps) => {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, { wait: debounceInMillis });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value);
  const handleClear = () => setValue('');

  useEffect(() => {
    if (debouncedValue !== initialValue) onChange(debouncedValue);
  }, [debouncedValue, initialValue, onChange]);

  return (
    <Input
      {...props}
      startIcon={<SearchRoundedIcon />}
      endIcon={clearSearch ? <Button data-testid='clear-search' variant='ghost' className='p-0' color='secondary' icon={<CloseRoundedIcon />} onClick={handleClear} /> : null}
      value={value}
      onChange={handleChange}
    />
  );
};

export default InputSearch;
